/* eslint-disable */
import React, { useCallback, useMemo, useState } from 'react'
import _ from 'lodash'
import {
  RangeSlider,
  RangeSliderTrack,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  HStack,
  VStack,
  Text as ChakraText,
  FormControl as ChakraFormControl,
} from '@chakra-ui/react'
import Select from 'react-select'
import type { SingleValue } from 'react-select'

import type {
  InputFormProps as Props,
  SizeFilterValue,
} from '../../../interfaces'
import {
  getFieldsMapping,
  getMaxFileSize,
} from '../../../../utils/mediaLibrary'

const SliderLabelText = ({ text }: { text: string }): React.ReactElement => (
  <ChakraText fontSize="smaller" color="#2d3748" fontWeight="medium">
    {text}
  </ChakraText>
)

const SliderLabels = ({
  value,
}: {
  value: SizeFilterValue
}): React.ReactElement => (
  <>
    <RangeSliderThumb zIndex="none" index={0}>
      <HStack spacing="0.25em" position="absolute" bottom="1.25em">
        <SliderLabelText text={value.range.min.toString()} />
        <SliderLabelText text={value.unit.toString()} />
      </HStack>
    </RangeSliderThumb>
    <RangeSliderThumb zIndex="none" index={1}>
      <HStack spacing="0.25em" position="absolute" bottom="1.25em">
        <SliderLabelText text={value.range.max.toString()} />
        <SliderLabelText text={value.unit.toString()} />
      </HStack>
    </RangeSliderThumb>
  </>
)

const SizeInputForm = (props: Props): React.ReactElement => {
  const {
    searchQueryJson,
    validationObject,
    groupId,
    filterId,
    setSearchQueryJson,
  } = props

  const [dirty, setDirty] = useState<boolean>(false)

  const expression = useMemo(
    () => searchQueryJson.filterGroups[groupId]?.expressions[filterId],
    [searchQueryJson, groupId, filterId],
  )

  const error: string | null = useMemo(
    () => validationObject.errors[groupId][filterId],
    [validationObject, groupId, filterId],
  )

  const handleRangeChange = useCallback(
    (newValue: { min: number; max: number }) => {
      // prettier-ignore
      const newSearchQueryJson = _.cloneDeep(searchQueryJson);
      // prettier-ignore
      (
        newSearchQueryJson.filterGroups[groupId].expressions[filterId]
          .value as SizeFilterValue
      ).range = newValue
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  const handleUnitChange = useCallback(
    (newUnit: string) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      if (
        (expression.value as SizeFilterValue).unit !== newUnit &&
        ((expression.value as SizeFilterValue).unit === 'MB' ||
          newUnit === 'MB')
      ) {
        // prettier-ignore
        (
          newSearchQueryJson.filterGroups[groupId].expressions[filterId]
            .value as SizeFilterValue
        ).range = {
          min: 0,
          max: getMaxFileSize(newUnit),
        }
      }
      // prettier-ignore
      (
        newSearchQueryJson.filterGroups[groupId].expressions[filterId]
          .value as SizeFilterValue
      ).unit = newUnit
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  return (
    <ChakraFormControl width="full" isInvalid={error !== null && dirty}>
      <VStack width="full" spacing="2.5em" alignItems="center">
        <VStack width="full" alignItems="end">
          <Select
            isSearchable={false}
            options={getFieldsMapping({
              fieldName: expression.fieldName,
            }).possibleUnits?.map(unit => ({
              value: unit,
              label: unit,
            }))}
            value={{
              value: (expression.value as SizeFilterValue).unit,
              label: (expression.value as SizeFilterValue).unit,
            }}
            onChange={(
              newVal: SingleValue<{
                label: string
                value: string
              }>,
            ) => {
              newVal?.value && handleUnitChange(newVal.value)
              setDirty(true)
            }}
          />
        </VStack>

        <RangeSlider
          width="90%"
          min={0}
          max={getMaxFileSize((expression.value as SizeFilterValue).unit)}
          value={[
            (expression.value as SizeFilterValue).range.min,
            (expression.value as SizeFilterValue).range.max,
          ]}
          onChange={values => {
            handleRangeChange({
              min: values[0],
              max: values[1],
            })
            setDirty(true)
          }}
        >
          <RangeSliderTrack>
            <RangeSliderFilledTrack />
          </RangeSliderTrack>
          <SliderLabels value={expression.value as SizeFilterValue} />
        </RangeSlider>
      </VStack>
    </ChakraFormControl>
  )
}

export default SizeInputForm
